import React, { memo, useRef } from 'react';
import { getIn, FormikProps } from 'formik';
import TextField, { TextFieldProps as UITextFieldProps } from '../TextField';
import { TextFieldProps } from '@mui/material/TextField';
import AiMenu from 'ui/domain/AiMenu/AiMenu';

export type FormikTextFieldProps<T> = {
  name: string;
  formikProps: {
    values: FormikProps<T>['values'];
    touched: FormikProps<T>['touched'];
    errors: FormikProps<T>['errors'];
    handleChange: FormikProps<T>['handleChange'];
    handleBlur: FormikProps<T>['handleBlur'];
    submitCount: FormikProps<T>['submitCount'];
    setFieldValue: FormikProps<T>['setFieldValue'];
  };
  aiMenu?: {
    enabled: boolean;
    companyId?: number;
    organizationId?: string;
  };
  'data-intercom-target'?: string;
} & UITextFieldProps;

type Props<T> = FormikTextFieldProps<T> &
  Omit<TextFieldProps, 'prefix'> & {
    variant?: 'standard' | 'filled';
  };

function FormikTextFieldComponent<T>(props: Props<T>) {
  const { formikProps, name, aiMenu, ...textFieldProps } = props;

  const fieldId = `${name}-input`;
  const error = !!getIn(formikProps.errors, name) && !!getIn(formikProps.touched, name);
  const helperText = error ? getIn(formikProps.errors, name) : props.helperText || ' ';

  const aiRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <TextField
        {...textFieldProps}
        name={name}
        id={fieldId}
        value={getIn(formikProps.values, name, '') ?? ''}
        error={error}
        sx={props.sx}
        onChange={formikProps.handleChange}
        onBlur={e => {
          formikProps.handleBlur(e);
          props.onBlur?.(e);
        }}
        helperText={helperText}
        suffixPosition={aiMenu?.enabled ? 'start-bottom' : props.suffixPosition}
        suffix={
          aiMenu?.enabled ? (
            <AiMenu
              companyId={aiMenu.companyId}
              organizationId={aiMenu.organizationId}
              input={getIn(formikProps.values, name, '') || ''}
              onReplace={value => {
                formikProps.setFieldValue(name, value);
              }}
              aiRef={aiRef}
            />
          ) : (
            props.suffix
          )
        }
      />
      {aiMenu?.enabled && <div ref={aiRef} />}
    </>
  );
}

function areEqual<T>(prevProps: Props<T>, nextProps: Props<T>) {
  const prevFormik = prevProps.formikProps;
  const nextFormik = nextProps.formikProps;

  const hasValueChanged = getIn(prevFormik.values, prevProps.name) !== getIn(nextFormik.values, nextProps.name);
  const hasTouchedChanged = getIn(prevFormik.touched, prevProps.name) !== getIn(nextFormik.touched, nextProps.name);
  const hasErrorChanged = getIn(prevFormik.errors, prevProps.name) !== getIn(nextFormik.errors, nextProps.name);
  const hasDisabledChanged = prevProps.disabled !== nextProps.disabled;
  const hasPrefixChanged = prevProps.prefix !== nextProps.prefix;
  const hasSxChanged = prevProps.sx !== nextProps.sx;

  return !(
    hasValueChanged ||
    hasTouchedChanged ||
    hasErrorChanged ||
    hasDisabledChanged ||
    hasPrefixChanged ||
    hasSxChanged
  );
}

export default memo(FormikTextFieldComponent, areEqual) as typeof FormikTextFieldComponent;
